/**
 * OnlyFans' GIF picker (Giphy through the Account's OnlyFans session, as the
 * CRM chat window's chat-composer-media.js does): trending first, search on
 * typing. Picking one stages it on the draft; the send carries its giphyId.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatherIcon } from '../components/FeatherIcon';
import { LoadingBlock } from '../components/Spinner';
import { crmFetch, dataOf } from './api';

const PAGE = 30;

export interface Gif {
  id: string;
  url: string;
  thumb: string;
  title: string;
}

type Raw = Record<string, unknown>;

function listOf(body: unknown): Raw[] {
  const payload = dataOf<Raw>(body) ?? {};
  const list = payload.list ?? payload.gifs ?? payload.data ?? (Array.isArray(payload) ? payload : []);
  return Array.isArray(list) ? (list as Raw[]) : [];
}

export function gifOf(g: Raw): Gif {
  const images = ((g.images || g.image || {}) as Record<string, Raw | undefined>) ?? {};
  const fixed = images.fixed_height || images.fixedHeight || images.preview_gif || images.previewGif || images.original || {};
  const url = String(fixed.url || fixed.webp || images.original?.url || g.url || '');
  const thumb = String(images.fixed_width_small?.url || images.fixed_height_small?.url || url);
  return { id: String(g.id || g.giphyId || g.giphy_id || ''), url, thumb, title: String(g.title || g.slug || '').trim() };
}

async function fetchGifs(accountId: string, fanId: string, query: string, offset: number): Promise<Gif[]> {
  const q = query.trim();
  const body = q
    ? await crmFetch('/v1/of/raw', {
        accountId,
        body: {
          method: 'GET',
          api_path: `/api2/v2/giphy/proxy/gifs/search?search=${encodeURIComponent(q)}&limit=${PAGE}&offset=${offset}`,
          referer: `https://onlyfans.com/my/chats/chat/${fanId}/`,
        },
      })
    : await crmFetch('/v1/giphy/trending', { accountId, body: { limit: PAGE, offset, fan_id: fanId } });
  return listOf(q ? dataOf(body) : body)
    .map(gifOf)
    .filter((g) => g.url && g.id);
}

interface Props {
  accountId: string;
  fanId: string;
  onPick: (gif: Gif) => void;
  onClose: () => void;
}

export function GifPicker({ accountId, fanId, onPick, onClose }: Props) {
  const { t } = useTranslation('chat');
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<Gif[]>([]);
  const [loading, setLoading] = useState(true);
  const [more, setMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const search = useRef<HTMLInputElement>(null);
  const seq = useRef(0);

  const load = async (q: string, offset: number) => {
    const mine = ++seq.current;
    setLoading(true);
    setError(null);
    try {
      const batch = await fetchGifs(accountId, fanId, q, offset);
      if (mine !== seq.current) return;
      setItems((prev) => (offset ? [...prev, ...batch] : batch));
      setMore(batch.length >= PAGE);
    } catch (err) {
      if (mine === seq.current) setError((err as Error).message);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  };

  useEffect(() => {
    search.current?.focus();
    const timer = window.setTimeout(() => void load(query, 0), query ? 350 : 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, accountId, fanId]);

  return (
    <div className="rc-pop rc-gif-pop">
      <div className="rc-pop-head">
        <FeatherIcon name="search" size={14} />
        <input
          ref={search}
          value={query}
          placeholder={t('gif.search')}
          autoComplete="off"
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Escape' && onClose()}
        />
        <button type="button" className="rc-pop-close" title={t('common.close')} onClick={onClose}>
          <FeatherIcon name="x" size={14} />
        </button>
      </div>
      <div
        className="rc-gif-grid"
        onScroll={(e) => {
          const el = e.currentTarget;
          if (!loading && more && el.scrollTop + el.clientHeight > el.scrollHeight - 120) void load(query, items.length);
        }}
      >
        {items.map((g) => (
          <button key={g.id} type="button" className="rc-gif-item" title={g.title || 'GIF'} onClick={() => onPick(g)}>
            <img src={g.thumb || g.url} alt={g.title || 'GIF'} loading="lazy" />
          </button>
        ))}
        {loading && <LoadingBlock pad="16px 0" label={t('gif.loading')} style={{ gridColumn: '1 / -1' }} />}
        {!loading && error && <div className="rc-pop-empty">{error}</div>}
        {!loading && !error && !items.length && <div className="rc-pop-empty">{t('gif.empty')}</div>}
      </div>
    </div>
  );
}
